import { useEffect, useState } from "react";
import type { Result } from "./searchTypes";
import { apiPath } from "./useApiResource";

export type SearchStatus = "idle" | "loading" | "ready" | "unavailable" | "error";

const DEBOUNCE_MS = 300;

// useSearch debounces query changes (GET /api/repos/{repo}/search runs a
// real server-side scan over ADR/design/runbook body text, unlike the
// client-side filtering every other index page in this app does — so unlike
// a plain <input onChange>, spamming a request per keystroke would be real,
// avoidable server load). An empty/whitespace query never fetches — the
// answer is always [], no need to round-trip for it.
//
// Search is repo-scoped, not cross-repo (see internal/search's package doc
// comment) — repo comes from useCurrentRepo() in every real call site, so
// searching while browsing cortex-core searches cortex-core, not always the
// default repo.
export function useSearch(token: string, repo: string, query: string): { results: Result[]; status: SearchStatus; message: string | null } {
  const [results, setResults] = useState<Result[]>([]);
  const [status, setStatus] = useState<SearchStatus>("idle");
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    const trimmed = query.trim();
    if (!trimmed) {
      setResults([]);
      setStatus("idle");
      setMessage(null);
      return;
    }

    let active = true;
    setStatus("loading");
    const timer = window.setTimeout(() => {
      fetch(apiPath(repo, `/search?q=${encodeURIComponent(trimmed)}`), { headers: { Authorization: `Bearer ${token}` } })
        .then(async (res) => {
          if (!active) return;
          if (!res.ok) {
            const body = (await res.json().catch(() => ({}))) as { error?: string };
            setStatus(res.status === 503 ? "unavailable" : "error");
            setMessage(body.error ?? `HTTP ${res.status}`);
            return;
          }
          const body = (await res.json()) as Result[];
          setResults(body);
          setStatus("ready");
        })
        .catch((e: unknown) => {
          if (!active) return;
          setStatus("error");
          setMessage(e instanceof Error ? e.message : "unknown error");
        });
    }, DEBOUNCE_MS);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [token, repo, query]);

  return { results, status, message };
}
